/**
 * Local persistence for dismissed conflict reasons.
 * Stored in localStorage with a 7-day TTL and minimal string ids.
 */

const STORAGE_KEY = 'dismissedConflicts_v1';
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

type Store = Record<string, number>; // id -> dismissedAt (epoch ms)

const safeNow = () => Date.now();

const loadStore = (): Store => {
  if (typeof window === 'undefined' || !window.localStorage) return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Store) : {};
    const now = safeNow();
    const cleaned: Store = {};
    Object.entries(parsed || {}).forEach(([id, ts]) => {
      if (typeof ts === 'number' && now - ts <= TTL_MS) {
        cleaned[id] = ts;
      }
    });
    if (Object.keys(cleaned).length !== Object.keys(parsed || {}).length) {
      persistStore(cleaned);
    }
    return cleaned;
  } catch {
    return {};
  }
};

const persistStore = (store: Store) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // ignore write errors (quota, private mode)
  }
};

let cache: Store | null = null;
const listeners = new Set<() => void>();

const notify = () => {
  listeners.forEach(fn => {
    try {
      fn();
    } catch {
      // ignore listener errors
    }
  });
};

const getStore = () => {
  if (!cache) cache = loadStore();
  return cache;
};

/**
 * Normalizes a conflict reason before generating the storage id so that
 * different UIs (list modal, card popover) resolve to the same key.
 * - Strips week annotations whether they appear as a prefix ("Week 12/2024:")
 *   or suffix (", week 12" or "(week 12)").
 * - Collapses whitespace and trims.
 */
const normalizeReasonText = (reason: string): string =>
  (reason || '')
    .replace(/^week\s*\d+(?:\/\d+)?[:\-\s]*/i, '') // drop leading "Week 12/2024: "
    .replace(/,\s*week\s*\d+(?:\/\d+)?/gi, '') // drop trailing ", week 12"
    .replace(/\(week\s*\d+(?:\/\d+)?\)/gi, '') // drop "(week 12)"
    .replace(/\s+/g, ' ')
    .trim();

export const makeConflictId = (itemId: string, reason: string): string => {
  const normalized = normalizeReasonText(reason)
    .toLowerCase()
    .slice(0, 80);
  return `${itemId}|${normalized}`;
};

export const isDismissed = (id: string): boolean => {
  const store = getStore();
  return Boolean(store[id]);
};

export const toggleDismissed = (id: string): boolean => {
  const store = getStore();
  if (store[id]) {
    delete store[id];
    persistStore(store);
    notify();
    return false;
  }
  store[id] = safeNow();
  persistStore(store);
  notify();
  return true;
};

export const cleanupDismissed = (): void => {
  cache = null;
  getStore(); // loadStore does cleanup
};

export const filterActiveReasons = <T extends { id: string }>(reasons: T[]): T[] =>
  reasons.filter(r => !isDismissed(r.id));

export const onDismissedChange = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Keep tabs in sync when localStorage updates elsewhere
if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (event.key === STORAGE_KEY) {
      cache = null;
      getStore();
      notify();
    }
  });
}

